import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  UserCircleIcon,
  CameraIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/auth';
import api from '../services/api';

interface ProfileForm {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  website?: string;
}

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export default function Profile() {
  const { affiliate, updateAffiliate } = useAuthStore();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors, isDirty },
  } = useForm<ProfileForm>({
    defaultValues: {
      name: affiliate?.name || '',
      email: affiliate?.email || '',
      phone: affiliate?.phone || '',
      company: affiliate?.company || '',
      website: affiliate?.website || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    watch: watchPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors },
  } = useForm<PasswordForm>();

  const newPassword = watchPassword('newPassword');

  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileForm) => {
      const response = await api.put('/auth/profile', data);
      return response.data;
    },
    onSuccess: (data) => {
      updateAffiliate(data.data);
      queryClient.invalidateQueries({ queryKey: ['affiliate'] });
      toast.success('Profile updated successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update profile');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data: PasswordForm) => {
      const response = await api.post('/auth/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      return response.data;
    },
    onSuccess: () => {
      resetPassword();
      toast.success('Password changed successfully!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to change password');
    },
  });

  const uploadAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('avatar', file);
      const response = await api.post('/auth/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: (data) => {
      updateAffiliate({ avatarUrl: data.data.avatarUrl });
      queryClient.invalidateQueries({ queryKey: ['affiliate'] });
      toast.success('Avatar updated!');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload avatar');
    },
  });

  const onProfileSubmit = (data: ProfileForm) => {
    updateProfileMutation.mutate(data);
  };

  const onPasswordSubmit = (data: PasswordForm) => {
    changePasswordMutation.mutate(data);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      uploadAvatarMutation.mutate(file);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Profile Settings</h1>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          Manage your account information and security settings
        </p>
      </div>

      {/* Avatar Section */}
      <div className="bg-[var(--bg-secondary)] rounded-lg shadow p-6">
        <div className="flex items-center gap-6">
          <div className="relative">
            {affiliate?.avatarUrl ? (
              <img
                src={affiliate.avatarUrl}
                alt={affiliate.name}
                className="h-24 w-24 rounded-full object-cover"
              />
            ) : (
              <div className="h-24 w-24 rounded-full bg-primary-100 flex items-center justify-center">
                <UserCircleIcon className="h-16 w-16 text-primary-600" />
              </div>
            )}
            <button
              onClick={handleAvatarClick}
              className="absolute bottom-0 right-0 p-2 bg-[var(--bg-secondary)] rounded-full shadow-lg border border-[var(--border-color)] hover:bg-[var(--bg-secondary)]"
            >
              <CameraIcon className="h-4 w-4 text-[var(--text-secondary)]" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">{affiliate?.name}</h2>
            <p className="text-sm text-[var(--text-muted)]">{affiliate?.email}</p>
            <p className="mt-1 text-sm text-[var(--text-muted)]">
              Referral Code: <span className="font-mono font-semibold text-primary-600">{affiliate?.referralCode}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-[var(--bg-secondary)] rounded-lg shadow">
        <div className="border-b border-[var(--border-color)]">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'profile'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-color)]'
              }`}
            >
              <UserCircleIcon className="h-5 w-5 inline mr-2" />
              Profile Information
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'password'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-color)]'
              }`}
            >
              <KeyIcon className="h-5 w-5 inline mr-2" />
              Change Password
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'profile' ? (
            <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary)]">
                    Full Name *
                  </label>
                  <input
                    {...registerProfile('name', { required: 'Name is required' })}
                    type="text"
                    className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  />
                  {profileErrors.name && (
                    <p className="mt-1 text-xs text-red-500">{profileErrors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary)]">
                    Email *
                  </label>
                  <input
                    {...registerProfile('email', { required: 'Email is required' })}
                    type="email"
                    className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  />
                  {profileErrors.email && (
                    <p className="mt-1 text-xs text-red-500">{profileErrors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary)]">
                    Phone
                  </label>
                  <input
                    {...registerProfile('phone')}
                    type="tel"
                    className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary)]">
                    Company
                  </label>
                  <input
                    {...registerProfile('company')}
                    type="text"
                    className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[var(--text-secondary)]">
                    Website
                  </label>
                  <input
                    {...registerProfile('website')}
                    type="url"
                    placeholder="https://"
                    className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={!isDirty || updateProfileMutation.isPending}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6 max-w-md">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Current Password *
                </label>
                <input
                  {...registerPassword('currentPassword', { required: 'Current password is required' })}
                  type="password"
                  className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-xs text-red-500">{passwordErrors.currentPassword.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  New Password *
                </label>
                <input
                  {...registerPassword('newPassword', {
                    required: 'New password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  })}
                  type="password"
                  className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                />
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-xs text-red-500">{passwordErrors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Confirm New Password *
                </label>
                <input
                  {...registerPassword('confirmPassword', {
                    required: 'Please confirm your new password',
                    validate: (value) => value === newPassword || 'Passwords do not match',
                  })}
                  type="password"
                  className="mt-1 block w-full rounded-md border border-[var(--border-color)] px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-sm"
                />
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-xs text-red-500">{passwordErrors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={changePasswordMutation.isPending}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-[var(--bg-secondary)] rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Account Information</h3>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-[var(--text-muted)]">Status</dt>
            <dd className="mt-1">
              <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                affiliate?.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : affiliate?.status === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {affiliate?.status ? affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1) : '-'}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-[var(--text-muted)]">Commission Type</dt>
            <dd className="mt-1 text-sm text-[var(--text-primary)]">{affiliate?.commissionType || 'CPA'}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-[var(--text-muted)]">Member Since</dt>
            <dd className="mt-1 text-sm text-[var(--text-primary)]">
              {affiliate?.createdAt
                ? new Date(affiliate.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '-'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-[var(--text-muted)]">Referral Code</dt>
            <dd className="mt-1 text-sm font-mono font-semibold text-primary-600">
              {affiliate?.referralCode}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
